import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import CustomInput from "../../components/CustomInput/CustomInput";
import regexList from "../../utils/regexList";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { signup } from "../../endpoints/auth.service";
import { useSnackbar } from "notistack";
import TickOutline from "../../assets/svg/TickOutline";
import LoadingAnimation from "../../assets/svg/LoadingAnimation";
import { getRecruiterInvitationByToken } from "../../endpoints/recruiter.service";
import { EnumUserTypes } from "@utils/constants";

const RegisterPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      isLoading: false,
      firstName: "",
      lastName: "",
      companyName: "",
      email: "",
      confirmEmail: "",
      invitationToken: "",
    },
  });

  const states = watch();

  const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
  const [selectedRole, setSelectedRole] = useState(params?.path || "employer");
  const invitationToken = searchParams.get("invitationToken");
  const isInviteMode = Boolean(invitationToken);

  useEffect(() => {
    const loadInvitation = async () => {
      if (!invitationToken) return;
      try {
        setValue("isLoading", true);
        const response = await getRecruiterInvitationByToken(invitationToken);
        if (response?.success && response?.data) {
          const invite = response.data;
          setSelectedRole("employer");
          setValue("firstName", invite?.firstName || "");
          setValue("lastName", invite?.lastName || "");
          setValue("companyName", invite?.company || "");
          setValue("email", invite?.email || "");
          setValue("confirmEmail", invite?.email || "");
          setValue("invitationToken", invitationToken);
        }
      } catch (error) {
        enqueueSnackbar(error || "Unable to load invitation.", {
          variant: "error",
        });
      } finally {
        setValue("isLoading", false);
      }
    };

    loadInvitation();
  }, [invitationToken, enqueueSnackbar, setValue]);

  const handleRegister = async (fields) => {
    try {
      setValue("isLoading", true);
      const payload = fields;
      let browserId = localStorage.getItem("browserId");
      if (!browserId) {
        browserId = uuidv4();
        localStorage.setItem("browserId", browserId);
      }
      payload.deviceId = browserId;
      payload.platform = "web";
      if (invitationToken) {
        payload.invitationToken = invitationToken;
      }

      const { data, success } = await signup({
        ...payload,
        userType: EnumUserTypes.CANDIDATE,
      });

      if (success) {
        localStorage.setItem("accessToken", data.token);
        if (!data?.verifiedEmail) {
          // navigate("/generate-user-name", {
          //   state: {
          //     email: fields?.email,
          //     firstName: fields?.firstName,
          //     lastName: fields?.lastName,
          //     isSignup: true,
          //     user: data,
          //   },
          //   replace: true,
          // });
          navigate(`/email-verification`, {
            state: {
              email: fields?.email,
            },
          });
          enqueueSnackbar("Please verify your account.", { variant: "success" });
          // navigate("/login");
        }
      }
      return;
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setValue("isLoading", false);
    }
  };

  const handleRoleChange = () => {
    setSelectedRole((prev) => {
      if (prev === "employer") {
        return "candidate";
      }
      return "employer";
    });
  };


  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="flex flex-col justify-center items-center border-[#DDDDDD] border rounded-lg py-10 px-20 relative bg-white w-max self-center justify-self-center overflow-y-scroll no-scrollbar">
        {!isInviteMode && (
          <button
            onClick={handleRoleChange}
            className="font-medium underline text-secondary mb-2 ml-10 absolute top-2 right-3"
          >
            {selectedRole === "employer"
              ? "Are you looking for job?"
              : "Are you an employer?"}
          </button>
        )}
        <label className="font-semibold text-3xl mb-2">
          Register as a candidate
        </label>
        <label className="text-light-black font-medium px-10">
          Create your account today and get started for free!
        </label>
        <form
          className="flex flex-col w-full pt-10 overflow-y-auto no-scrollbar h-max gap-1"
          onSubmit={handleSubmit(handleRegister)}
        >
          <div className="flex items-center justify-center gap-3">
            <CustomInput
              label={"First Name"}
              placeholder={"Enter your First Name"}
              name="firstName"
              rules={{
                required: "This field is required.",
              }}
              control={control}
              errors={errors}
              disabled={isInviteMode}
            />
            <CustomInput
              label={"Last Name"}
              placeholder={"Enter your Last Name"}
              name="lastName"
              rules={{
                required: "This field is required.",
              }}
              control={control}
              errors={errors}
              disabled={isInviteMode}
            />
          </div>
          <div className="h-3" />
          {selectedRole === "employer" ? (
            <>
              <CustomInput
                label={"Company Name"}
                placeholder={"Enter your Company Name"}
                name="companyName"
                rules={{
                  required: "This field is required.",
                }}
                control={control}
                errors={errors}
                disabled={isInviteMode}
              />
              <div className="h-3" />
            </>
          ) : null}
          <CustomInput
            label={"Email"}
            placeholder={"Enter your Email"}
            name="email"
            rules={{
              required: "This field is required.",
              pattern: {
                value: regexList.email,
                message: "Please enter a valid email.",
              },
            }}
            control={control}
            errors={errors}
            disabled={isInviteMode}
          />
          <div className="h-3" />
          <CustomInput
            label={"Confirm Email"}
            placeholder={"Enter your Confirm Email"}
            name="confirmEmail"
            rules={{
              required: "This field is required.",
              pattern: {
                value: regexList.email,
                message: "Please enter a valid email.",
              },
            }}
            control={control}
            errors={errors}
            disabled={isInviteMode}
          />
          <div className="h-3" />
          <div className="flex items-center justify-center gap-3">
            <CustomInput
              label={"Password"}
              placeholder={"Enter your Password"}
              name="password"
              rules={{
                required: "This field is required.",
                pattern: {
                  value: regexList.password,
                  message: "Please enter a valid password.",
                },
              }}
              control={control}
              errors={errors}
            />
            <CustomInput
              label={"Confirm Password"}
              placeholder={"Enter your Password"}
              name="confirmPassword"
              rules={{
                required: "This field is required.",
                pattern: {
                  value: regexList.password,
                  message: "Please enter a valid password.",
                },
              }}
              control={control}
              errors={errors}
            />
          </div>

          {/* Checkbox container  */}
          <div className="flex items-center gap-2 mt-3">
            <div
              onClick={() => setKeepMeSignedIn((prev) => !prev)}
              className="border-2 border-[#6F6F6F] h-4 w-4 flex items-center justify-center cursor-pointer"
            >
              {keepMeSignedIn ? (
                <TickOutline className="w-1.5 h-1.5" fill="#6F6F6F" />
              ) : null}
            </div>
            <span className="font-montserrat font-medium text-primary text-sm">
              I agree with <span className="underline">Terms of Use</span> and{" "}
              <span className="underline">Privacy Policy</span>
            </span>
          </div>

          <button
            type="submit"
            disabled={states?.isLoading || !keepMeSignedIn}
            className={`bg-primary hover:bg-black flex items-center justify-center rounded-full mt-10 py-3 ${
              !keepMeSignedIn ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {states?.isLoading && <LoadingAnimation />}
            <span className="text-white font-medium">Register</span>
          </button>
          <span className="mx-auto text-sm mt-1 font-medium text-light-black">
            Already have an account?{" "}
            <span
              onClick={() => navigate("/login")}
              className="text-primary font-semibold cursor-pointer"
            >
              Login
            </span>
          </span>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
